import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'

/**
 * Picks the alphabetical-predecessor slot for `name` among `siblings` and
 * returns the order value the new pill should occupy. Mirrors the MFR-side
 * computation in `createOptimisticPillNode` so the twin lands in the same
 * relative slot under BOD that its source lands under MFR.
 */
const computeAlphabeticalOrder = (
    name: string,
    siblings: Partial<ContentItemViewNodesType>[]
): number => {
    const trimmedName = name.trim()
    const predecessor = siblings.reduce<
        Partial<ContentItemViewNodesType> | undefined
    >((best, pill) => {
        const pillText = (pill.elements?.[0]?.text ?? pill.text ?? '').trim()
        const comesBefore =
            pillText.localeCompare(trimmedName, undefined, {
                sensitivity: 'base',
            }) < 0
        if (!comesBefore) return best
        return (pill.order ?? 0) > (best?.order ?? -Infinity) ? pill : best
    }, undefined)
    return predecessor
        ? (predecessor.order ?? 0) + 1
        : siblings.length > 0
        ? 0
        : 0
}

export interface BodTwinResult {
    twin: Partial<ContentItemViewNodesType>
    /** IDs of existing BOD siblings whose `order` must be shifted up by 1
     *  to avoid colliding with the twin's BOD-side slot. */
    shiftedBodSiblingIds: Set<string>
}

/**
 * Builds the BOD-paragraph twin of a freshly added MFR pill so it renders under
 * both the Manufacturers and BOD paragraphs at once. Mirrors the fetch-time
 * logic in useProductFetcher.utils.ts `appendBodContentIfNoBodPill`:
 * TreeDataMapper.findChildren treats `refBodItemId === bodParagraph.dtvItemId`
 * as the link that places a pill under BOD.
 *
 * The twin's `order` is computed against BOD siblings — not inherited from
 * the MFR pill — so it lands in its correct alphabetical slot among BOD
 * pills (Bug 2684545 / PR 192775 review). Returns the set of BOD-sibling
 * ids whose `order` must be shifted up by 1 to keep slots unique.
 *
 * Returns undefined when no matching BOD paragraph exists in the current view.
 */
export const buildBodTwinPill = (
    mfrPill: Partial<ContentItemViewNodesType>,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
): BodTwinResult | undefined => {
    const bodParagraph = contentItemViewNodes.find(
        (n) =>
            n.isBOD === true &&
            n.dtvContentItemParentId == null &&
            (!mfrPill.dtvParentId || n.dtvParentId === mfrPill.dtvParentId)
    )
    const bodDtvItemId = bodParagraph?.dtvItemId
    if (!bodDtvItemId) return undefined

    // BOD-side siblings: pills that already render under this BOD paragraph,
    // either as native BOD pills or via the refBodItemId twin link.
    const bodSiblings = contentItemViewNodes.filter(
        (n) =>
            n.id !== mfrPill.id &&
            n.dtvContentItemParentId != null &&
            (n.refBodItemId === bodDtvItemId || n.isBOD === true)
    )
    const twinName = mfrPill.elements?.[0]?.text ?? mfrPill.text ?? ''
    const twinOrder = computeAlphabeticalOrder(twinName, bodSiblings)

    const shiftedBodSiblingIds = new Set(
        bodSiblings
            .filter((n) => (n.order ?? 0) >= twinOrder && n.id != null)
            .map((n) => n.id as string)
    )

    const twinId = `${mfrPill.id}_bod_${bodDtvItemId}`
    return {
        twin: {
            ...mfrPill,
            id: twinId,
            order: twinOrder,
            refBodItemId: bodDtvItemId,
            isHidden: bodParagraph?.isHidden ?? false,
        },
        shiftedBodSiblingIds,
    }
}
